<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        ADDRESS_STORE,
        AddressForm,
        Heading,
        InputUrl,
        openModal,
        OrganizationForm,
        SelectChannels,
        SelectMarkets,
        SelectOrganizationTypes,
        SESSION_ORGANIZATION,
    } from '@smatch/cassiopeia-web-components';

    // STYLING
    import 'tailwindcss/tailwind.css';

    /* TYPES ----------------------------------------------------------------------------------- */

    import type { Address } from 'src/app/types';
    import type { SvelteComponent } from 'svelte';

    interface OrganizationForm extends SvelteComponent {
        isValid(): boolean;
        submit(): Promise<void>;
    }

    /* INTERNALS ------------------------------------------------------------------------------- */

    let organizationForm: OrganizationForm | null = null;

    const getAddress = (): Partial<Address> => {
        const ADDRESS = Object.values($ADDRESS_STORE).find(address => address.type === 'main');

        if (ADDRESS === undefined) {
            return { organizationKey: $SESSION_ORGANIZATION?._.key, type: 'main' };
        }
        return ADDRESS;
    };

    const ADDRESS: Partial<Address> = getAddress();
    const ORGANIZATION = { channelCodes: [], marketCodes: [], types: [], ...$SESSION_ORGANIZATION };

    $: hasTypes = $SESSION_ORGANIZATION !== null
        && Array.isArray($SESSION_ORGANIZATION.types)
        && $SESSION_ORGANIZATION.types.length > 0;

    $: hasChannels = $SESSION_ORGANIZATION !== null
        && Array.isArray($SESSION_ORGANIZATION.channelCodes)
        && $SESSION_ORGANIZATION.channelCodes.length > 0;

    $: hasMarketCodes = $SESSION_ORGANIZATION !== null
        && Array.isArray($SESSION_ORGANIZATION.marketCodes)
        && $SESSION_ORGANIZATION.marketCodes.length > 0;

    $: isOnlyWholesaler = Array.isArray(ORGANIZATION.types)
        && ORGANIZATION.types.includes('buyer_wholesaler')
        && ORGANIZATION.types.length === 1;

    $: hasSelectedTypes = Array.isArray(ORGANIZATION.types)
        && ORGANIZATION.types.length > 0;

    const onSave = async (): Promise<void> => {
        await (organizationForm as OrganizationForm).submit();
        openModal('#onboarding');
    };

</script>

<Heading size = 'md'>Company Information</Heading>

<OrganizationForm action = 'update' organization = { ORGANIZATION } bind:this = { organizationForm }>

    {#if !hasTypes}
        <SelectOrganizationTypes
            bind:keys = { ORGANIZATION.types }
            label = { null }
            placeholder = 'What best describes your business?'
            domain = 'buyer'
        />
    {/if}

    {#if hasSelectedTypes && !isOnlyWholesaler}

        {#if !hasChannels }
            <SelectChannels
                label = 'On which channels do you sell?'
                bind:keys = { ORGANIZATION.channelCodes }
            />
        {/if}

        {#if !hasMarketCodes}
            <SelectMarkets
                label = 'On which markets do you sell?'
                bind:keys = { ORGANIZATION.marketCodes }
                minItems = { 1 }
            />
        {/if}

    {/if}

    {#if ORGANIZATION.url === undefined }
        <InputUrl bind:value = { ORGANIZATION.url } isRequired = { false }/>
    {/if}

</OrganizationForm>

{#if ORGANIZATION._?.key !== undefined}
    <Heading size = 'sm'>Company Address</Heading>
    <AddressForm
        action = { '_' in ADDRESS ? 'update' : 'create' }
        address = { ADDRESS }
        on:cancel = { openModal.bind(null, '#onboarding') }
        on:save = { onSave }
        showCancel
        organizationKey = { ORGANIZATION._.key }
    />
{/if}